import React, { useState, useRef, useEffect, useCallback } from "react"
import {
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { Localized } from "@fluent/react"
import { RiskAnalysisDefect, RiskAnalysisFunction } from "../documents/riskAnalysis/riskAnalysisApi"
import { LoadingButton } from "@mui/lab"

function stripHtml(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent ?? div.innerText ?? ''
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface IshikawaProps {
    functions: RiskAnalysisFunction[]
    title: string
    onAddFunction: (name: string) => Promise<void>
    onUpdateFunction: (id: number, name: string) => Promise<void>
    onDeleteFunction: (id: number) => Promise<void>
    onAddDefect: (functionId: number, name: string) => Promise<void>
    onUpdateDefect: (id: number, name: string) => Promise<void>
    onDeleteDefect: (id: number) => Promise<void>
    onToggleCritical: (id: number, critical: boolean) => Promise<void>
    onGenAI: (fn: RiskAnalysisFunction) => Promise<void>
}

// ─── Canvas geometry ─────────────────────────────────────────────────────────

const CANVAS_WIDTH = 1200
const CANVAS_HEIGHT = 600
const SPINE_Y = 300
const SPINE_X_START = 60
const SPINE_X_END = 1160
const BONE_LENGTH = 260
const DEFECT_BRANCH_LEN = 80

interface BoneGeometry {
    functionId: number
    x1: number
    y1: number
    x2: number
    y2: number
    labelX: number
    labelY: number
    labelW: number
    labelH: number
}

function pointToSegmentDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1
    const dy = y2 - y1
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) return Math.hypot(px - x1, py - y1)
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

function drawDiagram(
    ctx: CanvasRenderingContext2D,
    functions: RiskAnalysisFunction[],
    selectedFunctionId: number | null,
    width: number,
    height: number,
    title: string,
): BoneGeometry[] {
    const spineY          = height / 2
    const spineXStart     = width * 0.05
    const spineXEnd       = width * 0.967
    const boneLength      = height * 0.433
    const defectBranchLen = width * 0.067

    ctx.clearRect(0, 0, width, height)

    // Spine
    ctx.strokeStyle = "#333"
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(spineXStart, spineY)
    ctx.lineTo(spineXEnd, spineY)
    ctx.stroke()

    // Arrowhead
    ctx.fillStyle = "#333"
    ctx.beginPath()
    ctx.moveTo(spineXEnd + 20, spineY)
    ctx.lineTo(spineXEnd, spineY - 8)
    ctx.lineTo(spineXEnd, spineY + 8)
    ctx.closePath()
    ctx.fill()

    // Title box — above arrowhead, right-aligned to spineXEnd
    const maxBoxWidth = functions.length > 0
        ? (spineXEnd - spineXStart - 60) / (functions.length + 1) + 30
        : (spineXEnd - spineXStart) * 0.3
    const titleFontSize = 15
    const padding = 8
    ctx.font = `${titleFontSize}px sans-serif`

    const words = title.split(' ')
    const titleLines: string[] = []
    let currentLine = ''
    for (const word of words) {
        const test = currentLine ? `${currentLine} ${word}` : word
        if (ctx.measureText(test).width > maxBoxWidth - 2 * padding && currentLine) {
            titleLines.push(currentLine)
            currentLine = word
        } else {
            currentLine = test
        }
    }
    if (currentLine) titleLines.push(currentLine)

    if (titleLines.length > 0 && title.trim()) {
        const lineHeight = titleFontSize + 4
        const boxWidth = Math.max(...titleLines.map(l => ctx.measureText(l).width)) + 2 * padding
        const boxHeight = titleLines.length * lineHeight + 2 * padding
        const boxX = spineXEnd - boxWidth
        const boxY = spineY - boxHeight - 30

        ctx.strokeStyle = '#333'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 5)
        ctx.stroke()

        ctx.fillStyle = '#000'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'top'
        titleLines.forEach((line, i) => {
            ctx.fillText(line, boxX + boxWidth / 2, boxY + padding + i * lineHeight)
        })
        ctx.textBaseline = 'alphabetic'
    }

    const bones: BoneGeometry[] = []
    if (functions.length === 0) return bones

    const spacing = (spineXEnd - spineXStart - 60) / (functions.length + 1)

    const wrapText = (text: string, maxWidth: number): string[] => {
        if (ctx.measureText(text).width <= maxWidth) return [text]
        const mid = Math.floor(text.length / 2)
        let bestIdx = -1
        let bestDist = Infinity
        for (let i = 0; i < text.length; i++) {
            if (text[i] === ' ') {
                const dist = Math.abs(i - mid)
                if (dist < bestDist) { bestDist = dist; bestIdx = i }
            }
        }
        if (bestIdx === -1) return [text]
        return [text.slice(0, bestIdx), text.slice(bestIdx + 1)]
    }

    functions.forEach((fn, idx) => {
        const attachX = spineXStart + 30 + spacing * (idx + 1)
        const isAbove = idx % 2 === 0
        const sign = isAbove ? -1 : 1

        // Diagonal tip (60°)
        const tipX = attachX - boneLength * Math.cos(Math.PI / 3)
        const tipY = spineY + sign * boneLength * Math.sin(Math.PI / 3)

        const isSelected = fn.id === selectedFunctionId
        ctx.strokeStyle = isSelected ? "#1976d2" : "#555"
        ctx.lineWidth = isSelected ? 3 : 2

        ctx.beginPath()
        ctx.moveTo(attachX, spineY)
        ctx.lineTo(tipX, tipY)
        ctx.stroke()

        // Function label
        const labelFont = isSelected ? "bold 13px sans-serif" : "13px sans-serif"
        ctx.font = labelFont
        ctx.textAlign = "center"
        const labelText = stripHtml(fn.name)
        const labelMetrics = ctx.measureText(labelText)
        const labelW = labelMetrics.width
        const labelH = 13
        const labelY = isAbove ? tipY - 10 : tipY + 18

        ctx.fillStyle = isSelected ? "#1976d2" : "#000"
        ctx.fillText(labelText, tipX, labelY)

        bones.push({
            functionId: fn.id,
            x1: attachX, y1: spineY, x2: tipX, y2: tipY,
            labelX: tipX, labelY, labelW, labelH,
        })

        // Defect branches (evenly spaced along the diagonal)
        const defectCount = fn.defects.length
        if (defectCount > 0) {
            const subSpacing = 1 / (defectCount + 1)
            fn.defects.forEach((defect, si) => {
                const t = subSpacing * (si + 1)
                const bx = attachX + t * (tipX - attachX)
                const by = spineY + t * (tipY - spineY)

                // Horizontal branch pointing left
                const branchEndX = bx - defectBranchLen
                const isCritical = defect.value === 1
                ctx.strokeStyle = isCritical ? "#d32f2f" : "#888"
                ctx.lineWidth =  1.5
                ctx.beginPath()
                ctx.moveTo(bx, by)
                ctx.lineTo(branchEndX, by)
                ctx.stroke()

                // Defect label
                ctx.fillStyle = isCritical ? "#d32f2f" : "#000"
                ctx.font = "12px sans-serif"
                ctx.textAlign = "right"
                const defectLines = wrapText(stripHtml(defect.name), spacing)
                if (defectLines.length === 1) {
                    ctx.fillText(defectLines[0], branchEndX, isAbove ? by - 4 : by + 14)
                } else {
                    if (isAbove) {
                        ctx.fillText(defectLines[0], branchEndX, by - 18)
                        ctx.fillText(defectLines[1], branchEndX, by - 4)
                    } else {
                        ctx.fillText(defectLines[0], branchEndX, by + 14)
                        ctx.fillText(defectLines[1], branchEndX, by + 28)
                    }
                }
            })
        }
    })

    return bones
}

// ─── SVG export ──────────────────────────────────────────────────────────────

function escSvg(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
}

export function generateIshikawaSVG(
    functions: RiskAnalysisFunction[],
    title: string,
    width = 1200,
    height = 600,
): string {
    const spineY = height / 2
    const spineXStart = width * 0.05
    const spineXEnd = width * 0.967
    const boneLength = height * 0.433
    const defectBranchLen = width * 0.067
    const charWidth = 7
    const titleFontSize = 15
    const padding = 8

    const els: string[] = []

    els.push(`<line x1="${spineXStart}" y1="${spineY}" x2="${spineXEnd}" y2="${spineY}" stroke="#333" stroke-width="3"/>`)
    els.push(`<polygon points="${spineXEnd + 20},${spineY} ${spineXEnd},${spineY - 8} ${spineXEnd},${spineY + 8}" fill="#333"/>`)

    const titleText = stripHtml(title)
    if (titleText.trim()) {
        const maxBoxWidth = functions.length > 0
            ? (spineXEnd - spineXStart - 60) / (functions.length + 1) + 30
            : (spineXEnd - spineXStart) * 0.3
        const words = titleText.split(' ')
        const titleLines: string[] = []
        let cur = ''
        for (const w of words) {
            const test = cur ? `${cur} ${w}` : w
            if (test.length * charWidth > maxBoxWidth - 2 * padding && cur) { titleLines.push(cur); cur = w }
            else cur = test
        }
        if (cur) titleLines.push(cur)
        const lineH = titleFontSize + 4
        const boxW = Math.max(...titleLines.map(l => l.length * charWidth)) + 2 * padding
        const boxH = titleLines.length * lineH + 2 * padding
        const boxX = spineXEnd - boxW
        const boxY = spineY - boxH - 30
        els.push(`<rect x="${boxX}" y="${boxY}" width="${boxW}" height="${boxH}" rx="5" fill="none" stroke="#333" stroke-width="1"/>`)
        titleLines.forEach((line, i) => {
            els.push(`<text x="${boxX + boxW / 2}" y="${boxY + padding + i * lineH + titleFontSize}" text-anchor="middle" font-size="${titleFontSize}" font-family="sans-serif" fill="#000">${escSvg(line)}</text>`)
        })
    }

    if (functions.length > 0) {
        const spacing = (spineXEnd - spineXStart - 60) / (functions.length + 1)

        const approxWrap = (text: string): string[] => {
            if (text.length * charWidth <= spacing) return [text]
            const mid = Math.floor(text.length / 2)
            let bestIdx = -1, bestDist = Infinity
            for (let i = 0; i < text.length; i++) {
                if (text[i] === ' ') {
                    const d = Math.abs(i - mid)
                    if (d < bestDist) { bestDist = d; bestIdx = i }
                }
            }
            return bestIdx === -1 ? [text] : [text.slice(0, bestIdx), text.slice(bestIdx + 1)]
        }

        functions.forEach((fn, idx) => {
            const attachX = spineXStart + 30 + spacing * (idx + 1)
            const isAbove = idx % 2 === 0
            const sign = isAbove ? -1 : 1
            const tipX = attachX - boneLength * Math.cos(Math.PI / 3)
            const tipY = spineY + sign * boneLength * Math.sin(Math.PI / 3)

            els.push(`<line x1="${attachX}" y1="${spineY}" x2="${tipX}" y2="${tipY}" stroke="#555" stroke-width="2"/>`)
            const labelY = isAbove ? tipY - 10 : tipY + 18
            els.push(`<text x="${tipX}" y="${labelY}" text-anchor="middle" font-size="13" font-family="sans-serif" fill="#000">${escSvg(stripHtml(fn.name))}</text>`)

            if (fn.defects.length > 0) {
                const sub = 1 / (fn.defects.length + 1)
                fn.defects.forEach((defect, si) => {
                    const t = sub * (si + 1)
                    const bx = attachX + t * (tipX - attachX)
                    const by = spineY + t * (tipY - spineY)
                    const ex = bx - defectBranchLen
                    const critical = defect.value === 1
                    const sc = critical ? '#d32f2f' : '#888'
                    const tc = critical ? '#d32f2f' : '#000'
                    els.push(`<line x1="${bx}" y1="${by}" x2="${ex}" y2="${by}" stroke="${sc}" stroke-width="1.5"/>`)
                    const lines = approxWrap(stripHtml(defect.name))
                    if (lines.length === 1) {
                        els.push(`<text x="${ex}" y="${isAbove ? by - 4 : by + 14}" text-anchor="end" font-size="12" font-family="sans-serif" fill="${tc}">${escSvg(lines[0])}</text>`)
                    } else {
                        if (isAbove) {
                            els.push(`<text x="${ex}" y="${by - 18}" text-anchor="end" font-size="12" font-family="sans-serif" fill="${tc}">${escSvg(lines[0])}</text>`)
                            els.push(`<text x="${ex}" y="${by - 4}" text-anchor="end" font-size="12" font-family="sans-serif" fill="${tc}">${escSvg(lines[1])}</text>`)
                        } else {
                            els.push(`<text x="${ex}" y="${by + 14}" text-anchor="end" font-size="12" font-family="sans-serif" fill="${tc}">${escSvg(lines[0])}</text>`)
                            els.push(`<text x="${ex}" y="${by + 28}" text-anchor="end" font-size="12" font-family="sans-serif" fill="${tc}">${escSvg(lines[1])}</text>`)
                        }
                    }
                })
            }
        })
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">\n${els.join('\n')}\n</svg>`
}

// ─── Component ───────────────────────────────────────────────────────────────

export const Ishikawa = (props: IshikawaProps) => {
    const {
        functions,
        title,
        onAddFunction,
        onUpdateFunction,
        onDeleteFunction,
        onAddDefect,
        onUpdateDefect,
        onDeleteDefect,
        onToggleCritical,
        onGenAI,
    } = props

    const canvasRef = useRef<HTMLCanvasElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const boneGeometryRef = useRef<BoneGeometry[]>([])

    const [canvasSize, setCanvasSize] = useState({ width: 900, height: 450 })
    const canvasSizeRef = useRef(canvasSize)
    useEffect(() => { canvasSizeRef.current = canvasSize }, [canvasSize])

    const [selectedFunctionId, setSelectedFunctionId] = useState<number | null>(null)
    const [newFunctionName, setNewFunctionName] = useState("")
    const [editingFunctionName, setEditingFunctionName] = useState("")
    const [newDefectName, setNewDefectName] = useState("")
    const [editingDefectId, setEditingDefectId] = useState<number | null>(null)
    const [editingDefectName, setEditingDefectName] = useState("")

    const [loading, setLoading] = useState(false)

    const selectedFunction = functions.find(f => f.id === selectedFunctionId) ?? null

    // Sync editingFunctionName when selection changes
    useEffect(() => {
        setEditingFunctionName(selectedFunction ? stripHtml(selectedFunction.name) : "")
        setEditingDefectId(null)
        setEditingDefectName("")
        setNewDefectName("")
    }, [selectedFunctionId, selectedFunction?.name])

    // Track container size
    useEffect(() => {
        const container = containerRef.current
        if (!container) return
        const ro = new ResizeObserver(entries => {
            const w = Math.round(entries[0].contentRect.width)
            setCanvasSize({ width: w, height: Math.round(w * 0.5) })
        })
        ro.observe(container)
        return () => ro.disconnect()
    }, [])

    // Redraw whenever data or selection changes
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext("2d")
        if (!ctx) return
        boneGeometryRef.current = drawDiagram(ctx, functions, selectedFunctionId, canvasSize.width, canvasSize.height, title)
    }, [functions, selectedFunctionId, canvasSize, title])

    const handleCanvasClick = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = canvasRef.current
            if (!canvas) return
            const rect = canvas.getBoundingClientRect()
            const scaleX = canvasSizeRef.current.width / rect.width
            const scaleY = canvasSizeRef.current.height / rect.height
            const px = (e.clientX - rect.left) * scaleX
            const py = (e.clientY - rect.top) * scaleY

            // Check label hit first
            for (const bone of boneGeometryRef.current) {
                const pad = 4
                if (
                    px >= bone.labelX - bone.labelW / 2 - pad &&
                    px <= bone.labelX + bone.labelW / 2 + pad &&
                    py >= bone.labelY - bone.labelH - pad &&
                    py <= bone.labelY + pad
                ) {
                    setSelectedFunctionId(bone.functionId)
                    return
                }
            }

            // Check bone (vertical line) hit
            let closest: { id: number; dist: number } | null = null
            for (const bone of boneGeometryRef.current) {
                const dist = pointToSegmentDist(px, py, bone.x1, bone.y1, bone.x2, bone.y2)
                if (!closest || dist < closest.dist) {
                    closest = { id: bone.functionId, dist }
                }
            }

            if (closest && closest.dist < 12) {
                setSelectedFunctionId(closest.id)
            } else {
                setSelectedFunctionId(null)
            }
        },
        []
    )

    const handleAddFunction = () => {
        const name = newFunctionName.trim()
        if (!name) return
        onAddFunction(name)
        setNewFunctionName("")
    }

    const handleSaveFunction = () => {
        if (!selectedFunction) return
        const name = editingFunctionName.trim()
        if (!name) return
        onUpdateFunction(selectedFunction.id, name)
    }

    const handleDeleteFunction = () => {
        if (!selectedFunction) return
        onDeleteFunction(selectedFunction.id)
        setSelectedFunctionId(null)
    }

    const handleAddDefect = () => {
        if (!selectedFunction) return
        const name = newDefectName.trim()
        if (!name) return
        onAddDefect(selectedFunction.id, name)
        setNewDefectName("")
    }

    const handleGenAiDefects = () => {
        if (!selectedFunction) return
        setLoading(true)
        onGenAI(selectedFunction).finally(() => setLoading(false))
    }

    const handleDefectEditBlur = (defect: RiskAnalysisDefect) => {
        const name = editingDefectName.trim()
        if (name && name !== defect.name) {
            onUpdateDefect(defect.id, name)
        }
        setEditingDefectId(null)
        setEditingDefectName("")
    }

    return (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            {/* Left: canvas */}
            <Box ref={containerRef} sx={{ width: "70vw", border: "1px solid #ddd", borderRadius: 1, overflow: "hidden" }}>
                <canvas
                    ref={canvasRef}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    style={{ display: "block", cursor: "pointer" }}
                    onClick={handleCanvasClick}
                />
            </Box>

            {/* Right: control panel */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Add function */}
                <Localized id="ishikawa-function-name" attrs={{ label: true }}>
                    <TextField
                        label="Function name"
                        size="small"
                        fullWidth
                        value={newFunctionName}
                        onChange={e => setNewFunctionName(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleAddFunction()}
                    />
                </Localized>
                <Localized id="ishikawa-add-function">
                    <Button variant="contained" size="small" onClick={handleAddFunction} disabled={!newFunctionName.trim()}>
                        Add function
                    </Button>
                </Localized>

                {/* Selected function editor */}
                {selectedFunction && (
                    <>
                        <Divider sx={{ my: 1 }} />

                        <Localized id="ishikawa-function-name" attrs={{ label: true }}>
                            <TextField
                                label="Function name"
                                size="small"
                                fullWidth
                                value={editingFunctionName}
                                onChange={e => setEditingFunctionName(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && handleSaveFunction()}
                            />
                        </Localized>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Localized id="save">
                                <Button variant="contained" size="small" onClick={handleSaveFunction}>
                                    Save
                                </Button>
                            </Localized>
                            <Localized id="ishikawa-delete-function">
                                <Button variant="outlined" color="error" size="small" onClick={handleDeleteFunction}>
                                    Delete function
                                </Button>
                            </Localized>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        <Localized id="ishikawa-defects">
                            <Typography variant="subtitle2">Defects</Typography>
                        </Localized>

                        <List dense disablePadding>
                            {selectedFunction.defects.map(defect => (
                                <ListItem key={defect.id} disableGutters>
                                    {editingDefectId === defect.id ? (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            autoFocus
                                            value={editingDefectName}
                                            onChange={e => setEditingDefectName(e.target.value)}
                                            onBlur={() => handleDefectEditBlur(defect)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") (e.target as HTMLInputElement).blur()
                                                if (e.key === "Escape") {
                                                    setEditingDefectId(null)
                                                    setEditingDefectName("")
                                                }
                                            }}
                                        />
                                    ) : (
                                        <>
                                            {/* <Localized id="ishikawa-critical" attrs={{ title: true }}> */}
                                                {/* <Tooltip title="Critical"> */}
                                                    <Checkbox
                                                        size="small"
                                                        checked={defect.value === 1}
                                                        onChange={e => onToggleCritical(defect.id, e.target.checked)}
                                                        sx={{ 
                                                            py: 0,
                                                        }}
                                                    />
                                                {/* </Tooltip> */}
                                            {/* </Localized> */}
                                            <ListItemText primary={stripHtml(defect.name)} />
                                            <ListItemSecondaryAction>
                                                {/* <Localized id="edit" attrs={{ title: true }}> */}
                                                    <IconButton
                                                        size="small"
                                                        // title="Edit"
                                                        onClick={() => {
                                                            setEditingDefectId(defect.id)
                                                            setEditingDefectName(stripHtml(defect.name))
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                {/* </Localized> */}
                                                {/* <Localized id="delete" attrs={{ title: true }}> */}
                                                    <IconButton
                                                        size="small"
                                                        //title="Delete"
                                                        color="error"
                                                        onClick={() => onDeleteDefect(defect.id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                {/* </Localized> */}
                                            </ListItemSecondaryAction>
                                        </>
                                    )}
                                </ListItem>
                            ))}
                        </List>

                        <Localized id="ishikawa-defect" attrs={{ label: true }}>
                            <TextField
                                label="Defect"
                                size="small"
                                fullWidth
                                value={newDefectName}
                                onChange={e => setNewDefectName(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && handleAddDefect()}
                            />
                        </Localized>
                        <Localized id="ishikawa-add-defect">
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleAddDefect}
                                disabled={!newDefectName.trim()}
                            >
                                Add defect
                            </Button>
                        </Localized>
                        {selectedFunction !== null && selectedFunction.defects.length === 0 && <Localized id="ishikawa-gen-ai-defect">
                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                size="small"
                                onClick={handleGenAiDefects}
                            >
                                Generuj z AI
                            </LoadingButton>
                        </Localized>}
                    </>
                )}
            </Box>
        </Box>
    )
}
